import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Layout from "@/components/layout/Layout";
import MainHeading from "@/components/common/MainHeading";
import Card from "@/components/common/Card";
import { Button } from "@/components/ui/button";
import { ThrowError } from "@/api/callApi";
import { SalaryHeadServices } from "@/services/SalaryHead/SalaryHeadServices";
import type { ISalaryHead } from "@/interface/salaryHead.interface";
import { ArrowLeft, Info } from "lucide-react";
import FullPageLoader from "@/components/common/FullPageLoader";

const ViewSalaryHead: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [salaryHead, setSalaryHead] = useState<ISalaryHead | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (!id) {
      navigate(-1);
      return;
    }

    const fetchData = async () => {
      try {
        const response = await SalaryHeadServices.getById(id);
        if (response.status && response.data) {
          setSalaryHead(response.data as ISalaryHead);
        }
      } catch (error) {
        ThrowError(error);
        navigate(-1);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [id, navigate]);



  if (isLoading) {
    return (
      <Layout>
        <FullPageLoader />
      </Layout>
    );
  }

  if (!salaryHead) {
    return (
      <Layout>
        <div className="text-center py-12">
          <h3 className="text-lg font-semibold text-gray-700">Salary head details not found</h3>
          <Button onClick={() => navigate(-1)} className="mt-4">
            <ArrowLeft className="w-4 h-4 mr-2" /> Back
          </Button>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6">
        <MainHeading
          title="View Salary Head"
          isBackButtonVisible
          onClick={() => navigate(-1)}
        />

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-6">
            <Card>
              <div className="flex items-center gap-2 mb-5 pb-3 border-b border-gray-100">
                <Info className="w-5 h-5 text-primary" />
                <h3 className="font-display text-base font-semibold">
                  Salary Head Information
                </h3>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-5">
                <InfoItem label="Name" value={salaryHead.name} capitalize />
                <InfoItem label="Type" value={salaryHead.type} capitalize />
                <InfoItem
                  label="Show in Offer Letter"
                  value={salaryHead.wantToShowInOfferLetter ? "Yes" : "No"}
                />
                <InfoItem
                  label="From Employer"
                  value={salaryHead.fromEmployeer ? "Yes" : "No"}
                />
                <InfoItem
                  label="Is Base"
                  value={salaryHead.isBase ? "Yes" : "No"}
                />
                <InfoItem
                  label="Is Taxable"
                  value={salaryHead.isTaxable ? "Yes" : "No"}
                />
                <InfoItem
                  label="Take Input"
                  value={salaryHead.takeInput ? "Yes" : "No"}
                />
                <InfoItem
                  label="Can Appear in Based On"
                  value={salaryHead.canAppearInBasedOn ? "Yes" : "No"}
                />
                <InfoItem
                  label="Max Cap"
                  value={salaryHead.maxCap ?? "—"}
                />
                <InfoItem
                  label="Amount Type"
                  value={salaryHead.amountType || "—"}
                  capitalize
                />
              </div>
            </Card>
          </div>

          <div className="space-y-6">
            <Card>
              <div className="flex items-center gap-2 mb-5 pb-3 border-b border-gray-100">
                <Info className="w-5 h-5 text-primary" />
                <h3 className="font-display text-base font-semibold">
                  Timeline
                </h3>
              </div>
              <div className="space-y-4">
                <InfoItem
                  label="Created At"
                  value={new Date(salaryHead.createdAt).toLocaleString()}
                />
                <InfoItem
                  label="Updated At"
                  value={new Date(salaryHead.updatedAt).toLocaleString()}
                />
              </div>
            </Card>
          </div>
        </div>
      </div>
    </Layout>
  );
};


const InfoItem: React.FC<{
  label: string;
  value?: string | number | null;
  capitalize?: boolean;
}> = ({ label, value, capitalize = false }) => (
  <div>
    <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-1">
      {label}
    </p>
    <p className={`text-sm font-semibold text-foreground ${capitalize ? "capitalize" : ""}`}>
      {value || "—"}
    </p>
  </div>
);

export default ViewSalaryHead;